import { ModifierProperty } from '@/enums';
import { INVALID_FIELDS } from '@/validations';
import i18next from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { useMemo } from 'react';
import * as Yup from 'yup';
import { useDetectConfigByPath } from './useDetectConfigByModuleName';

type SchemaShape = {
  [key: string]: any;
};
type FieldRequired = {
  [key: string]: boolean;
};
type ConfigObjectSchema = Yup.ObjectSchema<SchemaShape>;

export const useValidationForm = (
  formId: string,
  defaultSchemas: ConfigObjectSchema,
) => {
  const configurationModule = useDetectConfigByPath();

  const configFormData = useMemo(
    () => configurationModule.form?.[formId] ?? [],
    [formId, configurationModule],
  );

  //Transform schemas with required validation based on custom config data
  const transformRequiredValidation = useMemo(() => {
    const formSuffix = '-' + formId + '-input';

    const configRequired = configFormData?.reduce(
      (accumulator: Record<string, boolean>, config) => {
        if (isEmpty(config)) return accumulator;

        const fieldName = config.key.split(formSuffix)[0];

        const required = config?.modifier === ModifierProperty.MANDATORY;
        // const optional = config?.modifier === ModifierProperty.OPTIONAL;

        if (required) return { ...accumulator, [fieldName]: true };

        //* Temporary to disabled optional validation
        // if (optional) return { ...accumulator, [fieldName]: false };

        return accumulator;
      },
      {},
    );

    return configRequired;
  }, [configFormData, formId]);

  if (isEmpty(transformRequiredValidation)) {
    return defaultSchemas;
  }
  return generateValidationConfig(defaultSchemas, transformRequiredValidation);
};

const generateValidationConfig = (
  defaultSchemas: ConfigObjectSchema,
  requiredValidation: FieldRequired,
): ConfigObjectSchema => {
  const shape = defaultSchemas.fields;

  const updatedShape = Object.entries(shape).reduce(
    (acc, [shapeKey, schema]) => {
      if (schema instanceof Yup.ObjectSchema) {
        // Recursively handle nested objects schemas
        acc[shapeKey] = generateValidationConfig(schema, requiredValidation);
      } else {
        // Apply the required or optional logic to individual fields
        acc[shapeKey] = applyRequiredFlag(
          schema as Yup.AnySchema,
          requiredValidation[shapeKey],
        );
      }

      return acc;
    },
    {} as SchemaShape,
  );

  return defaultSchemas.shape(updatedShape);
};

// Helper function to apply required or optional based on requiredValidation
const applyRequiredFlag = (
  schema: Yup.AnySchema,
  isRequired: boolean | undefined,
): Yup.AnySchema => {
  if (isRequired) {
    if (schema instanceof Yup.ArraySchema) {
      // For arrays, enforce a minimum length of 1
      return schema.min(
        1,
        i18next.t(INVALID_FIELDS.MIN_NUMBER, { number: 1 }) as string,
      );
    }

    return schema.required(INVALID_FIELDS.REQUIRED);
  } else if (isRequired === false) {
    return (schema as Yup.AnySchema).optional();
  }

  // Keep the default schema if isRequired is undefined
  return schema;
};
